import draggable from 'vuedraggable';
import Widget from './widget.vue';
import cloneDeep from 'lodash/cloneDeep';
import api from '@/services/apiService/dashboards';
import { mapState } from 'vuex';
import debounce from 'lodash/debounce';
export default {
  name: 'DashboardWidgetGrid',
  components: {
    draggable,
    Widget
  },
  props: {
    dashboard: {
      type: Object,
      required: true
    },
    editMode: {
      type: Boolean,
      required: false,
      default: false
    },
    dateRange: {
      type: Object,
      required: true
    },
    customParameters: {
      type: Object,
      required: true
    },
    widgetMinSize: {
      type: Number,
      required: false,
      default: null
    }
  },
  data() {
    return {
      orderedWidgetCols: [],
      widgetIsBeeingDragged: false,
      widgetData: {},
      loadingWidgetData: {}
    };
  },
  computed: {
    ...mapState('dashboards', ['dashboards'])
  },
  watch: {
    dashboard: {
      handler(dashboard) {
        if (!dashboard) {
          this.orderedWidgetCols = [];
          return;
        }
        this.refreshData();
        const widgetCols = cloneDeep(dashboard.value.widgets.map(widget => {
          return {
            widget,
            id: widget.id,
            ...this.widgetSizeToGridSize(widget.size)
          };
        }));
        const sortedWidgetCols = widgetCols.sort((a, b) => {
          return a.widget.layoutOrderIdx - b.widget.layoutOrderIdx;
        });
        this.orderedWidgetCols = sortedWidgetCols;
      },
      deep: true,
      immediate: true
    },
    customParameters() {
      this.refreshData();
    },
    widgetIsBeeingDragged(isBeeingDragged) {
      if (isBeeingDragged) {
        return;
      }
      this.$nextTick(() => {
        this.$emit('change:widget-order', this.orderedWidgetCols.map((col, index) => ({
          widgetId: col.widget.id,
          layoutOrderIdx: index
        })));
      });
    }
  },
  methods: {
    getWidgetData(widgetId) {
      const data = this.widgetData[widgetId];
      if (!data) {
        return {};
      }
      return data;
    },
    widgetSizeToGridSize(widgetSize) {
      const finalWidgetSize = Math.max(widgetSize, this.widgetMinSize || widgetSize);
      switch (finalWidgetSize) {
        case 1:
          // S
          return {
            cols: 12,
            sm: 6,
            md: 3,
            lg: 3,
            xl: 2
          };
        case 2:
          // M
          return {
            cols: 12,
            sm: 12,
            md: 6,
            lg: 6,
            xl: 4
          };
        case 3:
          // L
          return {
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            xl: 6
          };
        case 4:
          // XL
          return {
            cols: 12,
            sm: 12,
            md: 12,
            lg: 12,
            xl: 12
          };
        default:
          throw new Error('Invalid widget size');
      }
    },
    setWidgetLoading(widgetId, loading) {
      this.loadingWidgetData = {
        ...this.loadingWidgetData,
        [widgetId]: loading
      };
    },
    refreshData: debounce(function () {
      this.actuallyRefreshData();
    }, 150),
    async actuallyRefreshData() {
      try {
        Object.keys(this.widgetData).forEach(widgetId => {
          this.setWidgetLoading(widgetId, true);
        });
        const newData = await api.fetchDataForAllWidgetsOfDashboard(this.dashboard.id, this.dateRange, this.customParameters);
        Object.entries(newData).forEach(([widgetId, dataSet]) => {
          this.widgetData[widgetId] = {
            success: true,
            data: dataSet
          };
        });
      } catch (e) {
        console.error('error', e);
        this.$snack(this.$t('Dashboards.components.dashboardWidgetGrid.snack.error.couldNotRefreshData', {
          error: e.message
        }), 'error');
      } finally {
        Object.keys(this.widgetData).forEach(widgetId => {
          this.setWidgetLoading(widgetId, false);
        });
      }
    },
    async refreshSingleWidgetData(widgetId) {
      try {
        this.setWidgetLoading(widgetId, true);
        const result = await api.fetchDataForWidget(this.dashboard.id, widgetId, this.dateRange, this.customParameters);
        this.widgetData[widgetId] = {
          success: true,
          data: result
        };
      } catch (e) {
        console.error('error', e);
        this.$snack(this.$t('Dashboards.components.dashboardWidgetGrid.snack.error.couldNotRefreshData', {
          error: e.message
        }), 'error');
        this.widgetData[widgetId] = {
          data: null,
          success: false,
          error: e
        };
      } finally {
        this.setWidgetLoading(widgetId, false);
      }
    }
  }
};